<script lang="ts" setup>
import { inject, ref } from "vue";
import { IItem, IOrderCart } from "../interfaces/cart.interfaces";
import { SITEURL } from "@libTs/cms.globals";
import axios, { AxiosResponse } from "axios";
import { EOverlay } from "../EOverlay";
import { ItemHelper as ItemHelperClass } from "../helpers/item.helper";
import { TranslationService } from "../services/translation.service";
import { OverlaysService } from "../services/overlays.service";
import { CartService } from "../services/cart.service";

const ItemHelper = inject<ItemHelperClass>("ItemHelper")!;
const t = inject<TranslationService>("translationSvc")!.t;
const overlaysSvc = inject<OverlaysService>("overlaysSvc")!;
const cartSvc = inject<CartService>("cartSvc")!;

interface IOrderHistory {
    DocEntry: number;
    DocDate: string;
    NumAtCard: string;
    VatSum: number;
    DiscSum: number;
    DocTotal: number;
    DocStatus: string;
    DocTime: string;
    Count: number;
    showDetails: boolean;
    // added for loading animation
    isLoadingItemDetails: boolean;
    // added by $scope.fetchOrderDetails(...)
    items: Array<IOrderHistoryDetail>;
}

interface IOrderHistoryDetail {
    DocEntry: number;
    LineNum: number;
    ItemCode: string;
    Dscription: string;
    Quantity: number;
    Price: number;
    Discprcnt: number;
    LineTotal: number;

    // added in API
    itemDetail?: IItem;
}

const { orders, count } = defineProps({
    orders: {
        type: Array<IOrderHistory>,
        required: true,
    },
    count: {
        type: Number,
        required: true,
    },
});

const isLoadingFirstItemsDetails = ref(true);
const isLoading = ref(false);
const skip = ref(0);
const take = ref(5);
const orderHistory = ref<Array<IOrderHistory>>([]);

orderHistory.value = orders;

const apiGetOrderHistoryDetails = (
    orderId: string,
): Promise<void | AxiosResponse<Array<IOrderHistoryDetail>>> => {
    return axios
        .post<Array<IOrderHistoryDetail>>(
            SITEURL + "actions/sapShop/order/order-details",
            {
                orderId: orderId,
            },
            {
                responseType: "json",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .catch(
            // error
            (err) => {
                console.error(err);
                console.warn("No response from server for orderId " + orderId);
            },
        );
};

const loadMore = (): void => {
    isLoading.value = true;
    skip.value += take.value;
    axios
        .get<Array<IOrderHistory>>(
            SITEURL +
                "actions/sapShop/order/orders?skip=" +
                skip.value +
                "&take=" +
                take.value,
            {
                responseType: "json",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .catch(
            // error
            () => {
                isLoading.value = false;
                console.error("No response from server");
            },
        )
        .then((data) => {
            if (data && data.status === 200) {
                const orders = data.data;
                orderHistory.value = orderHistory.value.concat(orders);
                fetchOrderDetails(skip.value, take.value);
            } else {
                console.error("Error response from server", data);
            }
            isLoading.value = false;
        });
};

const fetchOrderDetails = async (idxStart: number, count: number) => {
    if (typeof idxStart !== "number" || idxStart < 0) {
        idxStart = 0;
    }
    if (typeof count !== "number" || count < 5) {
        count = 5;
    }

    if (orderHistory.value && Array.isArray(orderHistory.value)) {
        const orders = orderHistory.value;
        const promises: Array<
            Promise<void | AxiosResponse<Array<IOrderHistoryDetail>>>
        > = [];
        for (let i = 0; i < idxStart + count && i < orders.length; ++i) {
            const order = orders[i];
            const orderId = order.DocEntry.toString();
            order.isLoadingItemDetails = true;
            promises.push(
                apiGetOrderHistoryDetails(orderId).then(
                    // success
                    (response) => {
                        if (response && Array.isArray(response.data)) {
                            order.items = response.data;
                        } else {
                            order.items = [];
                            console.warn(
                                `Cannot get order history details for order ${orderId}`,
                                response,
                            );
                        }
                        order.isLoadingItemDetails = false;
                    },
                    (err) => {
                        console.error(err);
                    },
                ),
            );
        }
        return Promise.all(promises)
            .then(() => {
                // console.log( 'order history details loaded' );
                isLoadingFirstItemsDetails.value = false;
            })
            .catch(() => {
                // console.log( 'order history details loaded' );
                isLoadingFirstItemsDetails.value = false;
            });
    }
};

const addAllItemsToCart = (
    orderItems?: Array<IOrderHistoryDetail>,
): boolean => {
    // console.log(orderItems);
    if (!Array.isArray(orderItems)) {
        return false;
    }
    const items: Array<IItem> = orderItems
        .filter(
            (oI) =>
                typeof oI.itemDetail === "object" &&
                oI.itemDetail.hasOwnProperty("ItemCode"),
        )
        .map((oI) => {
            const item = oI.itemDetail as IItem;
            item.quantity = 1;
            return item;
        });
    if (items.length === 0) {
        return false;
    }

    const event = new CustomEvent("addAllItemsToCartEvent", {
        detail: [
            {
                orderReference: "",
                orderName: "",
                currency: "",
                items: items,
                count: 0,
                totalPrice: 0,
            } as IOrderCart,
        ],
    });

    window.dispatchEvent(event);

    return true;
};

const formatDate = (date: string): string => {
    const d = new Date(date);

    return d.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};
const formatDateLong = (date: string): string => {
    const d = new Date(date);

    return d.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

await fetchOrderDetails(0, take.value);

const setDefaultQty = async (order: IOrderHistory) => {
    while (order.isLoadingItemDetails) {
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (!order.isLoadingItemDetails) {
            break;
        }
    }
    for (const item of order.items) {
        if (!item.itemDetail) {
            continue;
        }
        Object.assign(item.itemDetail, { qty: 1 });
    }
};

for (const order of orderHistory.value) {
    order.showDetails = false;
}
</script>

<template>
    <div class="">
        <div
            class="grid grid-cols-3 order-history-entry gap-y-4 mb-8"
            v-for="order in orderHistory"
        >
            <div class="">
                <span
                    class=""
                    :class="order.showDetails ? 'up' : 'down'"
                    @click="
                        order.showDetails = !order.showDetails;
                        setDefaultQty(order);
                    "
                ></span
                ><span class="text-normal">{{ order.DocEntry }}</span>
            </div>
            <div class="">
                <span class="">{{ formatDate(order.DocDate) }}</span>
                <!--d. MMMM yyyy-->
                <!--<span class="">{{ formatDateLong(order.DocDate) }}</span>-->
            </div>
            <div class="">
                <span class="font-bold">{{ order.DocTotal.toFixed(2) }}</span>
            </div>
            <div class="col-span-3 mb-4">
                <button
                    class="btn-cb btn-small"
                    @click="
                        order.showDetails = !order.showDetails;
                        setDefaultQty(order);
                    "
                >
                    {{ t("ORDER_HISTORY_SHOW_DETAILS") }}
                    <i
                        class="fa"
                        :class="
                            order.showDetails
                                ? 'fa-chevron-up'
                                : 'fa-chevron-down'
                        "
                    ></i>
                </button>
            </div>
            <div v-if="order.showDetails" class="col-span-3">
                <div class="flex justify-between items-center mb-4">
                    <h2>{{ t("ORDER_HISTORY_DETAILS") }}</h2>
                    <a
                        class="btn-cb btn-small"
                        v-if="
                            !order.isLoadingItemDetails &&
                            order.items &&
                            order.items.length
                        "
                        @click="
                            addAllItemsToCart(order.items) &&
                            overlaysSvc.showOverlay(
                                EOverlay.reorderItemsAddedToCart,
                            )
                        "
                        >{{ t("ADD_ALL_ITEMS_TO_CART_1X") }}</a
                    >
                </div>
                <div
                    v-if="!order.items"
                    class="loading-animation"
                    style="
                        width: 50px;
                        height: 50px;
                        margin-left: auto;
                        margin-right: auto;
                    "
                ></div>
                <div
                    class="order-history-detail-item mb-4 border-t border-black pt-4"
                    v-for="item in order.items"
                >
                    <div v-if="item.ItemCode == 'thp'">
                        {{ t("ORDER_HISTORY_THIRD_PARTY") }}
                    </div>
                    <div
                        v-if="item.ItemCode !== 'thp'"
                        class="flex flex-col gap-2"
                        :class="{
                            'color-orange':
                                !item.itemDetail || !item.itemDetail.ItemCode,
                        }"
                    >
                        <div class="">
                            {{ t("ORDER_HISTORY_ITEM_CODE") }}:
                            {{ item.ItemCode }}
                        </div>
                        <div>
                            {{ t("ORDER_HISTORY_ITEM_NAME") }}:
                            {{ item.Dscription }}
                        </div>
                        <div v-if="item.itemDetail?.BrandName">
                            {{ t("ORDER_HISTORY_ITEM_BRAND") }}:
                            {{ item.itemDetail?.BrandName }}
                        </div>
                        <div>
                            {{ t("ORDER_HISTORY_ITEM_QUANTITY") }}:
                            {{ item.Quantity }}
                        </div>
                        {{ t("ORDER_HISTORY_SINGLE_PRICE") }}: CHF
                        {{ item.Price.toFixed(2) }}<br />
                        <div v-if="item.itemDetail && item.itemDetail.ItemCode">
                            <div>
                                {{ t("ORDER_HISTORY_CURRENT_SINGLE_PRICE") }}:
                                CHF {{ item.itemDetail?.Preis.toFixed(2) }}
                            </div>
                            {{ t("ORDER_HISTORY_LEAD_TIME/STOCK") }}:
                            <span>{{
                                ItemHelper.availOrLeadText(
                                    item.itemDetail,
                                    t("PRODUCTS_UNITS"),
                                    t("PRODUCTS_ON_REQUEST"),
                                    t("PRODUCTS_WORKDAYS"),
                                    t("PRODUCTS_TO_BE_CONFIRMED"),
                                )
                            }}</span>
                        </div>
                        <div
                            v-if="item.itemDetail && item.itemDetail.ItemCode"
                            class="order-history-reorder w-auto"
                        >
                            <div class="flex flex-row items-center gap-4">
                                <button
                                    class="w-8 h-8 bg-grey-50 hover:bg-grey-10 rounded-s font-bold text-f6"
                                    @click="
                                        item.itemDetail.qty > 1
                                            ? item.itemDetail.qty--
                                            : null
                                    "
                                >
                                    -
                                </button>
                                <input
                                    type="number"
                                    v-model="item.itemDetail.qty"
                                    class="w-12 text-center border-none text-f6 font-bold quantity-input"
                                    min="1"
                                />
                                <button
                                    class="w-8 h-8 bg-grey-50 hover:bg-grey-100 rounded-s font-bold text-f6"
                                    @click="item.itemDetail.qty++"
                                >
                                    +
                                </button>
                                <button
                                    class="px-4 py-2 bg-primary hover:bg-primary-light text-white rounded-s flex flex-row items-center gap-4 text-center justify-center"
                                    @click.stop="
                                        cartSvc.addItemToCart(
                                            item.itemDetail,
                                            item.itemDetail.qty,
                                            undefined,
                                            false,
                                        )
                                    "
                                >
                                    <i class="fas fa-shopping-cart"></i>
                                </button>
                            </div>
                        </div>
                        <span
                            class="oldbtn-cb disabled"
                            v-if="!item.itemDetail || !item.itemDetail.ItemCode"
                            >{{ t("ITEM_DISCONTINUED") }}</span
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div>
        {{ t("PRODUCTS_RECORDS_LISTED") }}
        {{
            skip + take >= orderHistory.length
                ? orderHistory.length
                : skip + take
        }}
    </div>

    <br />

    <span
        class="btn-cb bgBlackFgWhite hoverBgWhiteFgBlack"
        :class="{ 'loading-animation': isLoading, disabled: isLoading }"
        @click="loadMore()"
    >
        <span class="text-normal">{{ t("LOAD_MORE_ENTRIES") }}</span>
    </span>
</template>
